import {
  setError, setIsAuthenticated, setIsLoaded, setIsSigningIn,
} from '@dtna/dvs-support-dcp-shared-state/lib/slices/login.slice';
import {
  getUserAuthInfo, getTan, setTan, TUserAuthInfo, setToken, signOut, TTokenResponse,
} from '@dtna/dvs-core-dcp-client-utils/lib/utils/browserStorage';
import AppConstants from '@dtna/dvs-core-dcp-client-utils/lib/constants/storage';
import { verify } from '@dtna/dvs-core-dcp-client-utils/lib/utils/jwt';
import IUserTanClaim from '@dtna/dvs-support-dcp-shared-state/lib/dtos/userTanClaim.dto';

import {
  customerLoadingFactory, IGetPrincipalResponse, IUserState, UNITS_SYSTEM_TYPES,
} from '@dtna/dvs-support-dcp-shared-state/lib/models/user.model';
import { IUser } from '@dtna/dvs-support-dcp-shared-state/lib/dtos/user.dto';
import { ICustomer } from '@dtna/dvs-support-dcp-shared-state/lib/dtos/customer.dto';
import IUserTan from '@dtna/dvs-support-dcp-shared-state/lib/dtos/useTan.dto';
import {
  userSetDataAction, userSetErrorAction, userSetHomePageAction, userSetIsAuthenticatedAction, userSetIsLoadedAction, userSetIsSigningInAction,
} from '@dtna/dvs-support-dcp-shared-state/lib/slices/user.slice';
import { setLegacy } from '@dtna/dvs-support-dcp-shared-state/lib/services/SessionStorage';
import { store } from '../store';
import proxyApi from './proxyApi';
import { PROXY_API } from '../constants/proxyApi';
import { ERROR_CLASS_500 } from '../constants/HttpErrorCodes';
import ERROR_DOMAIN from '../constants/errors';
import HOST_ROUTE_TABLE from '../constants/routes';

enum PORTAL_USER_TYPE { DCP, VT }

interface IUserAuthResult {
  authResponse: TUserAuthInfo;
  userType: PORTAL_USER_TYPE;
}

interface IUserTanClaimResult extends IUserAuthResult {
  userTanClaim?: IUserTanClaim;
}

const TOKEN_REFRESH_INTERVAL = 60000 * 5;

const verifyNeedsData = (state: any) => (userAuthInfo: TUserAuthInfo) => {
  // Prevents us from fetching unnessesary user data in the case of an app componet reload.
  if (userAuthInfo && !state.user.Model) {
    // Continue sign in
    return userAuthInfo;
  }

  throw new Error(ERROR_DOMAIN.USER_DATA_EXISTS);
};

const validateUserType = async (authResponse: TUserAuthInfo | void): Promise<IUserAuthResult> => {
  if (!authResponse) {
    throw new Error(ERROR_DOMAIN.NULL_AUTH_RESPONSE);
  }
  const { idp } = authResponse;
  if (idp === 'SAMLIdp') {
    return { authResponse, userType: PORTAL_USER_TYPE.VT };
  }
  return { authResponse, userType: PORTAL_USER_TYPE.DCP };
};

const getUserTan = async (userAuthResult: IUserAuthResult): Promise<IUserTanClaimResult> => {
  try {
    const { data } = await proxyApi.protectedRequest<IUserTanClaim>({
      url: PROXY_API.GET.USER.CLAIMS,
      method: 'GET',
    });
    const currentTan = getTan();
    if (currentTan === '') setTan(data.TanId);
    return { ...userAuthResult, userTanClaim: data };
  } catch (error) {
    if (userAuthResult.userType === PORTAL_USER_TYPE.VT) {
      return userAuthResult;
    }

    throw new Error(ERROR_DOMAIN.GENERIC_ERROR);
  }
};

const getUserModel = async (): Promise<{ Model: IUser, TANClaim: IUserTanClaim }> => {
  const tan = getTan();
  const url = PROXY_API.GET.USER.PRINCIPAL.replace(/:tan/, tan);
  const { data } = await proxyApi.protectedRequest<IGetPrincipalResponse>({ url });

  const Model = { ...data.UserModel };
  const TANClaim = { ...data.UserTanClaimModel };

  return {
    TANClaim,
    Model: { ...Model, FullName: `${Model.FirstName} ${Model.LastName}` } as IUser,
  };
};

const getGeofenceVehiclesCheck = async (): Promise<boolean> => {
  const tan = getTan();
  const uri = PROXY_API.GET.GEOFENCE.VEHICLES_WITH_GEOFENCE_CHECK.replace(/:tan/, tan);
  const { data } = await proxyApi.protectedRequest<boolean>({
    url: uri,
    method: 'GET',
  });

  return data;
};

const getCustomersModel = async (): Promise<ICustomer> => {
  const tan = getTan();
  const uri = PROXY_API.GET.CUSTOMER.WITH_TAN.replace(/:tan/, tan);
  const { data } = await proxyApi.protectedRequest<ICustomer>({
    url: uri,
    method: 'GET',
  });

  return data;
};

const getMultiFleetInfo = async (userTan: IUserTanClaim): Promise<IUserTan[]> => {
  const shouldFetch = !userTan.IsRoot && !userTan.IsDtna;
  if (shouldFetch) {
    const { data } = await proxyApi.protectedRequest<any>({
      url: PROXY_API.GET.CUSTOMER.PRINCIPAL,
      method: 'GET',
    });
    return data;
  }
  return [];
};

const setLegacyStorage = (userState: any) => {
  setLegacy({
    claims: userState.TANClaim.Claims,
    isAdmin: userState.TANClaim.IsAdmin,
    isDtna: userState.TANClaim.IsDtna,
    isRoot: userState.TANClaim.IsRoot,
    isXota: userState.TANClaim.IsXota,
    isSafety: userState.TANClaim.IsSafety,
    isCsAdmin: userState.TANClaim.IsCsAdmin,
    isOps: userState.TANClaim.IsOps,
    tanId: userState.TANClaim.TanId,
    tanName: userState.TANClaim.TanName,
    profile: { UserModel: { ...userState.Model }, TanClaimModel: { ...userState.TANClaim } },
    potaModalVersionSeen: userState.Model.PotaModalVersionSeen,
    infoModalVersionSeen: userState.Model.InfoModalVersionSeen,
    hasSafetySubscription: userState.Customer.PkgSafetySubscription,
    hasFuelSubscription: userState.Customer.PkgFuelSubscription,
    canResolveFaults: userState.TANClaim.CanResolveFaults,
    oid: userState.Model.OID,
  });
};

const getUserData = async (userTan: IUserTanClaim) => {
  const data: Partial<IUserState> = await Promise.all([
    getUserModel(),
    getCustomersModel(),
    getMultiFleetInfo(userTan),
    getGeofenceVehiclesCheck(),
  ]).then((results) => {
    const { Model, TANClaim } = results[0];
    const Customer = results[1];
    const UserTANs = results[2];
    const hasVehiclesWithGeofence: boolean = results[3];
    return {
      Customer,
      Model,
      TANClaim,
      UserTANs,
      customerLoading: customerLoadingFactory(),
      httpRequestError: false,
      unitsSystemType: Model.State?.unitsSystemType ?? UNITS_SYSTEM_TYPES.IMPERIAL,
      hasVehiclesWithGeofence,
    };
  }).catch((error) => {
    if (ERROR_CLASS_500.includes(error.response?.status)) {
      throw new Error(ERROR_DOMAIN.GENERIC_ERROR);
    }
    throw new Error(ERROR_DOMAIN.GET_USER_DATA_FAILED);
  });
  setLegacyStorage(data);
  userSetDataAction(data)(store.dispatch);
  userSetIsAuthenticatedAction(true)(store.dispatch);
  store.dispatch(setIsAuthenticated(true));
  return data;
};

const processUser = async (userTanClaimResult: IUserTanClaimResult): Promise<Partial<IUserState>> => {
  const { userTanClaim } = userTanClaimResult;
  if (undefined === userTanClaim) {
    throw new Error();
  }
  const result = await getUserData(userTanClaim);
  return result;
};

const handleSignInRedirect = (fromRefresh?: boolean) => {
  if (fromRefresh !== true) {
    const redirectUri = HOST_ROUTE_TABLE.LEGACY.ROOT;
    userSetHomePageAction(redirectUri)(store.dispatch);
  }
  userSetIsLoadedAction(true)(store.dispatch);
  userSetIsSigningInAction(false)(store.dispatch);
  store.dispatch(setIsLoaded(true));
  store.dispatch(setIsSigningIn(false));
};

const handleUserSignInError = ({ message }: Error) => {
  store.dispatch(setIsLoaded(true));
  store.dispatch(setIsSigningIn(false));
  userSetIsLoadedAction(true)(store.dispatch);
  userSetIsSigningInAction(false)(store.dispatch);
  if (message !== ERROR_DOMAIN.AUTH.TOKEN_NOT_FOUND && message !== ERROR_DOMAIN.USER_DATA_EXISTS) {
    store.dispatch(setError(message));
    userSetErrorAction(message)(store.dispatch);
  }
};

const refreshToken = (adalResponse: TTokenResponse): Promise<TTokenResponse> => new Promise((resolve, reject) => {
  proxyApi.request<any>({
    url: PROXY_API.GET.SIGN_IN.REFRESH_TOKEN,
    headers: { 'Application-Token': adalResponse.refreshToken },
    method: 'GET',
  })
    .then((response) => {
      resolve({ ...adalResponse, bearerToken: response.data.id_token, refreshToken: response.data.refresh_token });
    })
    .catch(reject);
});

const getTokenResponse = (): Promise<TTokenResponse> => new Promise((resolve, reject) => {
  const token = localStorage.getItem(AppConstants.LOCAL_STORAGE.TELEMATICS.TOKEN);
  if (!token) {
    reject(new Error('token not found'));
    return;
  }
  verify(token, (err?: Error, decoded?: TTokenResponse) => {
    if (err) {
      reject(err);
      return;
    }
    resolve(decoded!);
  });
});

const checkTokenValidity = () => getTokenResponse()
  .then((response) => {
    const { signatureTimestamp } = response;
    const currentTime = Date.now();
    const stale = currentTime - signatureTimestamp;
    if (stale > TOKEN_REFRESH_INTERVAL) {
      refreshToken(response)
        .then(setToken)
        .catch(() => signOut());
    }
  })
  .catch((err) => {
    // Don't sign out if there is no token to clear.
    if (err.message !== ERROR_DOMAIN.AUTH.TOKEN_NOT_FOUND) {
      signOut();
    }
  });

const handleUserSignIn = async (fromRefresh?: boolean) => {
  store.dispatch(setIsLoaded(false));
  store.dispatch(setIsSigningIn(false));

  userSetIsLoadedAction(false)(store.dispatch);
  userSetIsSigningInAction(true)(store.dispatch);

  checkTokenValidity();

  getUserAuthInfo()
    .then(verifyNeedsData(store.getState()))
    .then(validateUserType)
    .then(getUserTan)
    .then(processUser)
    .then(() => handleSignInRedirect(fromRefresh))
    .catch(handleUserSignInError);
};

export default handleUserSignIn;
